<script setup lang="ts">
import { ref, onBeforeMount, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { MAP_ROLE_TO_DEPARTMENT } from '@/constants';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import useUserInfo from '@/modules/user/useUserInfo';

interface Props {
  deptChangedName: any;
}
const props = defineProps<Props>();

const emits = defineEmits<{ staticEvent: [value: string] }>();

const { t } = useI18n({ useScope: 'global' });

const AdminQuoteStore = useAdminQuoteStore();

const deptStatus = computed(() => props?.deptChangedName?.value);

const userInfo = useUserInfo();
const userRolePublic = ref<any>('');
const statistic = computed<any>(() => AdminQuoteStore.statistic);
const data = ref();
const commonPermissions = ['CO', 'SU', 'AO', 'CR', 'IN', 'SADMIN', 'GQT_SUPPORT', 'ACCOUNTING'];
const quoteStatus = [
  { key: 'NEW', labelKey: 'my_quote.received', extraPermissions: ['GC', 'RM'] },
  { key: 'IN_PROGRESS', labelKey: 'quote_status.in_progress', extraPermissions: ['GC', 'RM'] },
  { key: 'PRICE_MATCH', labelKey: 'quote_status.price_match', extraPermissions: ['RM'] },
  { key: 'QUOTE_READY', labelKey: 'quote_status.quote_ready' },
  { key: 'RQ_TO_BOOK', labelKey: 'quote_status.rq_to_book' }
];
const bookingStatus = [
  { key: 'BOOKED', labelKey: 'quote_status.booked' },
  { key: 'DEPOSIT_DUE', labelKey: 'quote_status.deposit_due' },
  { key: 'COMPLETE_NAMES', labelKey: 'quote_status.complete_names' },
  { key: 'FINAL_PAYMENT_DUE', labelKey: 'management.email.final_payment_due' },
  { key: 'BOOKING_COMPLETED', labelKey: 'quote_status.booking_completed' },
  { key: 'BOOKING_CANCELLED', labelKey: 'quote_status.booking_cancelled', statusOverride: 'BOOKING_COMPLETED' }
];

watch(deptStatus, () => {
  AdminQuoteStore.getStatistic();
});

onBeforeMount(async () => {
  await AdminQuoteStore.getStatistic();
  const currentUser = await userInfo.getUserInfo();
  const userRole = currentUser?.role?.toUpperCase();
  if (MAP_ROLE_TO_DEPARTMENT[userRole]) {
    userRolePublic.value = MAP_ROLE_TO_DEPARTMENT[userRole];
  } else {
    userRolePublic.value = userRole;
  }
});

const handleSelectStatus = (status: string) => {
  emits('staticEvent', status);
};

const createManagementData = (statusList: any, newVal: any, basePermissions: string[]) => {
  return statusList.map((status: any) => ({
    label: t(status.labelKey),
    permissions: [...basePermissions, ...(status.extraPermissions || [])],
    value: newVal[status.key],
    lastVisit: newVal[`${status.key}_LAST_VISIT`],
    status: status.statusOverride || status.key
  }));
};

watch(
  statistic,
  (newVal) => {
    if (newVal) {
      const dataQuoteManagement = createManagementData(quoteStatus, newVal, commonPermissions);
      const dataBookingManagement = createManagementData(bookingStatus, newVal, commonPermissions);
      data.value = [
        {
          label: t('menu.quote_management'),
          value: dataQuoteManagement,
          permissions: [...commonPermissions, 'GC', 'RM']
        },
        {
          label: 'Booking management',
          value: dataBookingManagement,
          permissions: commonPermissions
        }
      ];
    }
  },
  { immediate: true }
);
</script>
<template>
  <div class="statics-wrap">
    <div v-for="(item, index) in data" :key="index">
      <div v-if="item.permissions.includes(userRolePublic)">
        <div class="title">
          <i class="pi pi-users mr-3"></i>
          <b>{{ userRolePublic }} - {{ item.label }} </b><br />
        </div>
        <div class="surface-ground">
          <div class="grid">
            <div id="admin-stats-co" class="col-12 md:col-6 lg:col-2 cursor-pointer" v-for="(itemStatic, indexStatic) in item.value" :key="indexStatic" @click="handleSelectStatus(itemStatic.status)">
              <a class="no-underline" v-if="itemStatic.permissions.includes(userRolePublic)">
                <div class="surface-card shadow-2 p-3 border-round">
                  <div class="flex justify-content-between mb-3">
                    <div>
                      <div class="h-4rem">
                        <span class="block font-medium mb-3">{{ itemStatic.label.toUpperCase() }}</span>
                      </div>
                      <div class="text-900 font-medium text-xl">{{ itemStatic.value }}</div>
                    </div>
                  </div>
                  <span class="text-primary font-medium">{{ itemStatic.lastVisit }} </span>
                  <span> {{ ' ' + $t('status.since_last_visit') }}</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="view-all">
      <span class="view-all-text" @click="handleSelectStatus('ALL')">{{ $t('quote_status.view_all_statuses') }}</span>
    </div>
  </div>
</template>
